import { TRIMMED_PIECE_SUFFIX } from "../squareBaseBuilder/chimneyTrim";
import type { AssemblyFilmStoryboard } from "./storyboard";

/**
 * Step index for a built mesh, from its engine name. The first step whose
 * prefix matches wins, so broad prefixes (`detail/framing/`) may sit on a
 * later step as a sweep. Unmatched names go to the catch-all step, so the
 * last frame of the film is always the whole sauna.
 */
export function classifyAssemblyMesh(
	storyboard: AssemblyFilmStoryboard,
	name: string,
): number {
	const { steps } = storyboard;
	for (let index = 0; index < steps.length; index++) {
		if (steps[index].meshPrefixes.some((prefix) => name.startsWith(prefix))) {
			return index;
		}
	}
	const catchAll = steps.findIndex((step) => step.catchAll);
	return catchAll >= 0 ? catchAll : steps.length - 1;
}

/** Names nothing claimed explicitly — the catch-all's leftovers. */
export function findUnclaimedAssemblyMeshes(
	storyboard: AssemblyFilmStoryboard,
	names: readonly string[],
): string[] {
	return names.filter(
		(name) =>
			!storyboard.steps.some((step) =>
				step.meshPrefixes.some((prefix) => name.startsWith(prefix)),
			),
	);
}

export type AssemblyFilmKitCounts = Record<string, Record<string, number>>;

/**
 * Piece counts for the kit box, counted from the built scene: one mesh is
 * one piece for every family listed in a kit item's `countPrefixes`. A mesh
 * only counts toward the step it was classified into, so a broad sweep
 * prefix can never double-count a piece an earlier step already showed.
 */
export function countAssemblyKitParts(
	storyboard: AssemblyFilmStoryboard,
	names: readonly string[],
): AssemblyFilmKitCounts {
	const counts: AssemblyFilmKitCounts = {};
	for (const step of storyboard.steps) counts[step.id] = {};
	for (const name of names) {
		// The far piece of a member cut around a chimney flue is the same board
		// as the near one (skorstensväxling): one piece in the kit, not two.
		if (name.endsWith(TRIMMED_PIECE_SUFFIX)) continue;
		const step = storyboard.steps[classifyAssemblyMesh(storyboard, name)];
		for (const item of step.kit) {
			if (item.kind !== "part" || !item.countPrefixes) continue;
			if (!item.countPrefixes.some((prefix) => name.startsWith(prefix))) {
				continue;
			}
			counts[step.id][item.id] = (counts[step.id][item.id] ?? 0) + 1;
		}
	}
	return counts;
}
